import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {SCOPE_LABELS, type PermissionScope} from '@common/permissions';
import {ScopeBadge} from '@internal/components/roles-permissions/ScopeBadge';
import {SCOPE_FILTER_ORDER} from '@internal/components/roles-permissions/utils';
import type {PermissionListItem} from '@internal/components/roles-permissions/types';
import {RefreshCw, ShieldCheck, Loader2} from 'lucide-react';

type PermissionsFilters = {scope: string};

const scopeOptions = SCOPE_FILTER_ORDER.filter((s) => s !== 'all').map((s) => ({
  value: s,
  label: SCOPE_LABELS[s as PermissionScope] ?? s,
}));

const permissionColumns: Column<PermissionListItem>[] = [
  {
    id: 'name',
    header: 'Permission',
    render: (p) => (
      <div className='flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-brand-navy'>{p.name}</span>
        {p.description && (
          <span className='text-xs text-brand-gray line-clamp-1 max-w-md'>{p.description}</span>
        )}
      </div>
    ),
  },
  {
    id: 'scope',
    header: 'Who is this for?',
    render: (p) => <ScopeBadge scope={p.scope} />,
  },
];

export function PermissionsList({embedded = false}: {embedded?: boolean} = {}) {
  const table = useDataTable<PermissionsFilters>({
    defaultPageSize: 50,
    defaultFilters: {scope: ''},
    searchDebounce: 400,
    syncToUrl: 'perms',
  });

  const {data, isLoading, isFetching, error, refetch} = trpc.internal.permissions.list.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    search: table.debouncedSearch.trim() ? table.debouncedSearch.trim() : undefined,
    scope: table.filterValues.scope ? (table.filterValues.scope as PermissionScope) : undefined,
  });

  const dataTable = (
    <DataTable<PermissionListItem, PermissionsFilters>
      table={table}
      columns={permissionColumns}
      data={data?.permissions}
      total={data?.totalCount}
      totalPages={data?.totalPages}
      loading={isLoading}
      error={error}
      emptyMessage='No permissions match the current filters.'
      title='All permissions'
      entity={{singular: 'permission', plural: 'permissions'}}
      searchPlaceholder='Search by name or description'
      filters={[
        {
          key: 'scope',
          label: 'User types',
          type: 'select',
          placeholder: 'Who is this for?',
          options: scopeOptions,
        },
      ]}
      rowKey={(p) => p.id}
      rowHref={(p) => `/permissions/${p.id}`}
    />
  );

  const body = (
    <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
      {embedded && (
        <div className='flex items-center justify-end gap-2 mb-4'>
          {isFetching && !isLoading && <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />}
          <Button variant='outline' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </Button>
        </div>
      )}
      {dataTable}
    </div>
  );

  if (embedded) return body;

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Permissions'
          description='Control what actions users can perform. Built-in permissions are managed by the application; use this page to review and adjust their settings.'
          icon={<ShieldCheck className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction tone='secondary' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </PageHeaderAction>
        </PageHeader>

        {body}
      </div>
    </div>
  );
}
